import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceStatus,
  IExperience,
  IExperienceTemplate,
  MomentTemplateStatus
} from '@dadohr/shared/domainTypes/experience';
import { CustomFieldKey, IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import { IUser, StandardUserFieldKey } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import assertNever from 'assert-never';
import {
  Button,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';
import { compact, keyBy, sortBy } from 'lodash';
import { ExternalLink, Info, Pencil, Pin, PinOff } from 'lucide-react';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DataGridItem,
  DataGridVertical
} from '../../../../components/DataGrid';
import { Color, ColoredChipDark } from '../../../../components/ColoredChip';
import { InfoBox } from '../../../../components/InfoBox';
import {
  InlineLoader,
  Loader,
  PageLoader
} from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { NotFound } from '../../../../components/NotFound';
import { SomethingWentWrongWithReport } from '../../../../components/SomethingWentWrong';
import { TableSearch } from '../../../../components/Table';
import { TabPanel } from '../../../../components/Tabs';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer, FlexContainerVertical } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  DEFAULT_OFFSET,
  PageToolbar,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import {
  useRoutes,
  useStringQueryParam,
  useTypedStringQueryParam
} from '../../../../routes';
import {
  isDadoEmailAddress,
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../services/currentUser';
import { useExperienceInstancesByUserId } from '../../../../services/experiences/instance';
import { getTemplateExpectedEndThreshold } from '../../../../services/experiences/template';
import { useExperienceTemplatesMinimalByOrgId } from '../../../../services/experiences/templateMinimal';
import {
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../../services/firebase';
import {
  pinUserProfileField,
  useOrgUiSettings
} from '../../../../services/orgUiSettings';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import {
  formatDateHuman,
  now,
  timestampToMillisSafe
} from '../../../../services/time';
import { useEmployeeAppDashboardUrlForUserId } from '../../../../services/user/employeeAppDashboardUrl';
import {
  toFullName,
  toSearchTermRegexp,
  useUserById
} from '../../../../services/user';
import {
  ExperienceCard,
  ExperienceCardChipsRow,
  ExperienceCardGrid,
  ExperienceCardHeader,
  ExperienceCardMetaRow,
  ExperienceCardStatusGroup,
  ExperienceCardStatusHeading,
  ExperienceCardTitle
} from '../../../Experiences/components/ExperienceCard';
import { ParticipantDialogLazy } from '../../../Experiences/pages/Details/ParticipantDialog';
import {
  MomentCommonCardStyle,
  MomentTemplateStatusAndDateColumn,
  MomentTemplateTitleColumn
} from '../../../Moments/pages/Overview';
import {
  getMomentsAudience,
  useMomentInstancesByUserId,
  useMomentTemplatesByOrgId
} from '../../../Moments/services';
import {
  EditFieldDialog,
  FieldType,
  getFieldType
} from '../../components/EditFieldDialog';
import {
  MergeClashResolution,
  MergeEmployeeButton
} from '../../components/MergeEmployee';
import {
  useAllUserDataGridFields,
  UserDataGrid
} from '../../components/UserDataGrid';
import {
  getProfileExperienceArchiveDate,
  getProfileExperienceStartDate,
  groupProfileExperiences,
  ProfileExperienceGroup
} from './experienceGroups';
import { useUserHistoryData, useUserImportHistory } from './service';

// Reserves the small chip's height so Active cards without a chip (e.g. a
// finished instance) line up with those that have one.
const ChipsRow = styled(ExperienceCardChipsRow)`
  min-height: 24px;
`;

// Profile-only spacing: a little more room between the title and the dates,
// and under each section heading, than the overview grid uses. && beats
// MuiTypography-root's `margin: 0`, which Material UI injects after emotion.
const MetaRow = styled(ExperienceCardMetaRow)`
  && {
    margin-top: ${(p) => p.theme.spacing()}px;
  }
`;

const StatusHeading = styled(ExperienceCardStatusHeading)`
  && {
    margin-bottom: ${(p) => p.theme.spacing(3)}px;
  }
` as typeof ExperienceCardStatusHeading;

const DateLines = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing(0.5)}px;
`;

// The label ("Started:") sits at the theme's medium weight; the date itself
// keeps body1's regular weight.
const DateLineLabel = styled('span')`
  font-weight: ${(p) => p.theme.typography.fontWeightMedium};
`;

const DateLine = ({ label, ts }: { label: string; ts: Timestamp }) => (
  <Typography variant="body1">
    <DateLineLabel>{label}</DateLineLabel> {formatDateHuman(ts, true)}
  </Typography>
);

// Chip shown in the card header. Active cards reflect whether the instance is
// on schedule; a finished instance still inside its archive window counts as
// on time. Upcoming and Archived cards are labelled as such.
const getCardChip = (
  group: ProfileExperienceGroup,
  status: ExperienceStatus,
  t: UnsafeTFunction
): [string, Color] | null => {
  switch (group) {
    case 'active':
      if (status === ExperienceStatus.LATE) {
        return [t('Overdue'), 'red'];
      }
      if (
        status === ExperienceStatus.RUNNING ||
        status === ExperienceStatus.COMPLETE
      ) {
        return [t('On Time'), 'blue'];
      }
      return null;
    case 'upcoming':
      return [t('Upcoming'), 'gray'];
    case 'archived':
      return [t('Archived'), 'gray'];
    default:
      return assertNever(group);
  }
};

const ProfileExperienceCard = ({
  d,
  tpl,
  group
}: {
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  group: ProfileExperienceGroup;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const [dialogInstanceId, setDialogInstanceId] = useStringQueryParam('i', '');
  const dialogOpen = dialogInstanceId === d.id;

  const start = getProfileExperienceStartDate(d.data);
  const chip = getCardChip(group, d.data.status, t);

  return (
    <>
      <ExperienceCard
        variant="outlined"
        clickable
        onClick={() => {
          trackEvent({
            eventType: PAEventType.EXPERIENCE_INSTANCE,
            name: 'Admin Instance View Loaded',
            'Instance ID': d.id,
            Trigger: 'User Profile',
            Explanation: 'Admin user views the details of a single instance'
          });
          setDialogInstanceId(d.id);
        }}
      >
        <ExperienceCardHeader>
          <ChipsRow>
            {chip && (
              <ColoredChipDark color={chip[1]} size="small">
                {chip[0]}
              </ColoredChipDark>
            )}
          </ChipsRow>
        </ExperienceCardHeader>

        <ExperienceCardTitle variant="h4" component="h4">
          {tpl.data.adminTitle || tpl.data.title}
        </ExperienceCardTitle>

        <MetaRow>
          <DateLines>
            {group === 'active' && (
              <>
                <DateLine label={t('Started:', { nsSeparator: false })} ts={start} />
                <DateLine
                  label={t('Due to end:', { nsSeparator: false })}
                  ts={d.data.expectedEnd}
                />
              </>
            )}
            {group === 'upcoming' && (
              <DateLine
                label={t('Due to start:', { nsSeparator: false })}
                ts={start}
              />
            )}
            {group === 'archived' && (
              <>
                <DateLine label={t('Started:', { nsSeparator: false })} ts={start} />
                <DateLine
                  label={t('Archived:', { nsSeparator: false })}
                  ts={getProfileExperienceArchiveDate(
                    d.data,
                    getTemplateExpectedEndThreshold(tpl.data)
                  )}
                />
              </>
            )}
          </DateLines>
        </MetaRow>
      </ExperienceCard>
      {dialogOpen && (
        <ParticipantDialogLazy
          instanceId={d.id}
          tplId={d.data.templateId}
          open
          onClose={() => setDialogInstanceId('')}
        />
      )}
    </>
  );
};

const USER_DATA_GRID_MINIMAL_FIELDS: StandardUserFieldKey[] = [
  'email',
  'personalEmail'
];

const ExperiencesToolbar = styled('div')`
  display: flex;
  margin-bottom: ${(p) => p.theme.spacing(3)}px;
`;

const UserProfileExperiences = ({
  userId,
  org
}: {
  userId: string;
  org: IOrganization;
}) => {
  const { t } = useUnsafeTranslation();
  const [instances, loading, error] = useExperienceInstancesByUserId(
    org.id,
    userId
  );

  if (loading) {
    return <Loader height={200} />;
  }
  if (error) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'user_profile', section: 'experiences' }}
      />
    );
  }
  if (!instances) {
    return null;
  }
  if (!instances.length) {
    return (
      <Centered height={200}>
        <Typography variant="caption">
          {t('This user has not taken part in any experiences yet')}
        </Typography>
      </Centered>
    );
  }
  return <UserProfileExperiencesList instances={instances} orgId={org.id} />;
};

// Separate from UserProfileExperiences so the org-wide template fetch only
// runs once the user is known to have instances.
const UserProfileExperiencesList = ({
  instances,
  orgId
}: {
  instances: Doc<IExperience>[];
  orgId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [tpls, loading, error] = useExperienceTemplatesMinimalByOrgId(orgId);
  const [q, setQ] = useState('');

  const tplById = useMemo(() => keyBy(tpls || [], (tpl) => tpl.id), [tpls]);
  const getTpl = useCallback(
    (templateId: string) => tplById[templateId]?.data,
    [tplById]
  );

  const allGroups = useMemo(
    () => (tpls ? groupProfileExperiences(instances, getTpl, now()) : null),
    [instances, tpls, getTpl]
  );

  const groups = useMemo(() => {
    if (!allGroups) {
      return null;
    }
    if (!q) {
      return allGroups;
    }
    const re = toSearchTermRegexp(q);
    const matches = (d: Doc<IExperience>) => {
      const tpl = tplById[d.data.templateId];
      return !!tpl && !!(tpl.data.adminTitle || tpl.data.title || '').match(re);
    };
    return {
      active: allGroups.active.filter(matches),
      upcoming: allGroups.upcoming.filter(matches),
      archived: allGroups.archived.filter(matches)
    };
  }, [allGroups, tplById, q]);

  if (loading && !groups) {
    return <Loader height={200} />;
  }
  if (error) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'user_profile', section: 'experiences' }}
      />
    );
  }
  if (!allGroups || !groups) {
    return null;
  }

  const hasAny =
    allGroups.active.length + allGroups.upcoming.length + allGroups.archived.length >
    0;
  if (!hasAny) {
    return (
      <Centered height={200}>
        <Typography variant="caption">
          {t('This user has not taken part in any experiences yet')}
        </Typography>
      </Centered>
    );
  }

  const hasMatches =
    groups.active.length + groups.upcoming.length + groups.archived.length > 0;

  const renderGroup = (
    title: string,
    group: ProfileExperienceGroup,
    items: Doc<IExperience>[]
  ) =>
    items.length ? (
      <ExperienceCardStatusGroup>
        <StatusHeading variant="h2" component="h2">
          {title}
        </StatusHeading>
        <ExperienceCardGrid>
          {items.map((d) => (
            <ProfileExperienceCard
              key={d.id}
              d={d}
              tpl={tplById[d.data.templateId]}
              group={group}
            />
          ))}
        </ExperienceCardGrid>
      </ExperienceCardStatusGroup>
    ) : null;

  return (
    <>
      <ExperiencesToolbar>
        <TableSearch term={q} onChange={setQ} placeholder={t('Search by name')} />
      </ExperiencesToolbar>
      {!hasMatches && (
        <Centered height={200}>
          <Typography variant="caption">
            {t('No experiences match your search')}
          </Typography>
        </Centered>
      )}
      {renderGroup(t('Active experiences'), 'active', groups.active)}
      {renderGroup(t('Upcoming experiences'), 'upcoming', groups.upcoming)}
      {renderGroup(t('Archived experiences'), 'archived', groups.archived)}
    </>
  );
};

const MomentTemplateContainer = styled(MomentCommonCardStyle)((p) => ({
  width: '100%',
  gridTemplateColumns: '1fr 1fr 0.5fr 1fr'
}));

const UserProfileMoments = ({
  user,
  org
}: {
  user: Doc<IUser>;
  org: IOrganization;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();

  const [tpls, loading, error] = useMappedLoadingValue(
    useCombineLoadingValues(
      useMomentTemplatesByOrgId(org.id),
      useMomentInstancesByUserId(org.id, user.id)
    ),
    ([xs, insts]) => {
      const n = now();
      const instsByTplId = keyBy(insts, (i) => i.data.tplId);

      return sortBy(
        compact(
          xs.map((x) => {
            if (x.data.status === MomentTemplateStatus.COMPLETE) {
              const inst = instsByTplId[x.id];
              if (inst) {
                const audience =
                  x.data.content.audiences.find(
                    (a) => a.id === inst.data.audience
                  ) || null;
                return {
                  tpl: x,
                  audience,
                  instance: inst,
                  type: 'SENT' as const
                };
              }
            } else {
              const audience = getMomentsAudience(x, user, n) || null;
              return { tpl: x, audience, type: 'NOT_SENT' as const };
            }
            return null;
          })
        ),
        (x) => -x.tpl.data.sendDate.at.toMillis()
      );
    }
  );

  console.log(tpls, error);

  return (
    <>
      {loading && <Loader height={200} />}
      {error && (
        <SomethingWentWrongWithReport
          error={error}
          context={{ page: 'user_profile', section: 'moments' }}
        />
      )}
      {tpls && !tpls.length && (
        <Centered height={200}>
          <Typography variant="caption">
            {t('This user did not take part in any moments yet')}
          </Typography>
        </Centered>
      )}
      {!!tpls?.length && (
        <FlexContainerVertical spacing={2} fullWidth alignItems="stretch">
          {tpls.map((tpl) => {
            return (
              <Link
                key={tpl.tpl.id}
                to={ROUTES.moments.details.url({
                  momentTemplateId: tpl.tpl.id
                })}
              >
                <MomentTemplateContainer>
                  <MomentTemplateTitleColumn d={tpl.tpl} />
                  <MomentTemplateStatusAndDateColumn d={tpl.tpl} org={org} />
                  <Typography variant="caption">
                    {tpl.audience?.name}
                  </Typography>
                  <FlexContainer
                    justifyContent="flex-end"
                    alignItems="center"
                    fullWidth
                  >
                    <Button
                      color="primary"
                      endIcon={<ExternalLink size={16} />}
                    >
                      {t('View')}
                    </Button>
                  </FlexContainer>
                </MomentTemplateContainer>
              </Link>
            );
          })}
        </FlexContainerVertical>
      )}
    </>
  );
};

const PRIORITIZED_FIELDS: (StandardUserFieldKey | CustomFieldKey)[] = [
  'email',
  'personalEmail',
  'location',
  'role',
  'department',
  'hireDate',
  'managerId'
];

const FieldActionContainer = styled(FlexContainer)((p) => ({
  '> button': {
    display: 'none'
  },
  ':hover': {
    '> button': {
      display: 'block'
    }
  }
}));

type EditingField = {
  key: string;
  type: FieldType;
  label: string;
  isCustomField: boolean;
} | null;

const mergeSavedFieldIntoUser = (
  user: Doc<IUser>,
  fieldPath: string,
  value: unknown
): Doc<IUser> => {
  if (fieldPath.startsWith('customFields.')) {
    const key = fieldPath.replace('customFields.', '');
    return {
      ...user,
      data: {
        ...user.data,
        customFields: {
          ...user.data.customFields,
          [key]: value
        } as IUser['customFields']
      }
    };
  }
  return {
    ...user,
    data: {
      ...user.data,
      [fieldPath]: value
    }
  };
};

const UserProfileData = ({
  user,
  org,
  pinnedFields,
  lastSavedField,
  onFieldSaved
}: {
  user: Doc<IUser>;
  org: IOrganization;
  pinnedFields: (StandardUserFieldKey | CustomFieldKey)[];
  lastSavedField: { fieldPath: string; value: unknown } | null;
  onFieldSaved: (fieldPath: string, value: unknown) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { customUserFields, dateFormats, id: orgId } = org;
  const [editingField, setEditingField] = useState<EditingField>(null);

  const displayUser = useMemo(
    () =>
      lastSavedField
        ? mergeSavedFieldIntoUser(
            user,
            lastSavedField.fieldPath,
            lastSavedField.value
          )
        : user,
    [user, lastSavedField]
  );

  const gridFields = useAllUserDataGridFields(customUserFields);
  const ds: DataGridItem[] = useMemo(() => {
    const withActions = (
      k: StandardUserFieldKey | CustomFieldKey,
      x: DataGridItem | null
    ): DataGridItem | null => {
      if (!x) {
        return null;
      }
      const isPinned = pinnedFields.includes(k);
      const fieldType = getFieldType(k, customUserFields);
      const isCustomField = k.startsWith('customFields.');
      const actualKey = isCustomField ? k.replace('customFields.', '') : k;
      const customFieldDef = isCustomField
        ? customUserFields.find((f) => f.key === actualKey)
        : undefined;
      const isSmartDate = customFieldDef?.type === 'smartDate';
      const isSmartText = customFieldDef?.type === 'smartText';
      const isComputedField = isSmartDate || isSmartText;
      const smartDateSourceLabel =
        isSmartDate && customFieldDef?.type === 'smartDate'
          ? (gridFields[
              customFieldDef.sourceField as StandardUserFieldKey | CustomFieldKey
            ]?.(displayUser, t, dateFormats) as DataGridItem | null)?.label ||
            customFieldDef.sourceField
          : null;

      return {
        label: x.label,
        d: (
          <FieldActionContainer>
            <div>{x.d || '-'}</div>
            {isComputedField ? (
              <Tooltip
                title={
                  isSmartDate
                    ? t(
                        'The value of this field is automatically calculated based on {{ sourceDate }}',
                        { sourceDate: smartDateSourceLabel }
                      ) as string
                    : t('The value of this field is automatically calculated based on rules') as string
                }
              >
                <IconButton size="small">
                  <Info size={12} />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton
                size="small"
                onClick={() =>
                  setEditingField({
                    key: actualKey,
                    type: fieldType,
                    label: x.label as string,
                    isCustomField
                  })
                }
                title={t('Edit')}
              >
                <Pencil size={12} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => pinUserProfileField(orgId, k, !isPinned)}
              title={isPinned ? t('Unpin') : t('Pin')}
            >
              {isPinned ? <PinOff size={12} /> : <Pin size={12} />}
            </IconButton>
          </FieldActionContainer>
        )
      };
    };
    const firstBatch = compact(
      PRIORITIZED_FIELDS.map((f) =>
        withActions(f, gridFields[f](displayUser, t, dateFormats))
      )
    );
    const secondBatch = compact(
      (Object.keys(gridFields) as (keyof typeof gridFields)[]).map((k) =>
        PRIORITIZED_FIELDS.includes(k)
          ? null
          : withActions(k, gridFields[k](displayUser, t, dateFormats))
      )
    );
    return [
      ...firstBatch,
      { label: '', d: <div>&#8203;</div> },
      ...sortBy(secondBatch, (x) => x.label)
    ];
  }, [
    gridFields,
    displayUser,
    dateFormats,
    t,
    orgId,
    pinnedFields,
    customUserFields
  ]);

  return (
    <>
      <DataGridVertical ds={ds} />
      {editingField && (
        <EditFieldDialog
          open={true}
          onClose={() => setEditingField(null)}
          user={user}
          fieldKey={editingField.key}
          fieldType={editingField.type}
          fieldLabel={editingField.label}
          orgId={orgId}
          isCustomField={editingField.isCustomField}
          onSaved={onFieldSaved}
        />
      )}
    </>
  );
};

const ImportChangesGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(0),
  alignItems: 'flex-end'
}));

/**
 * Resolves the admin who performed a merge. If the executor is a Dado team
 * member, we mask their identity as "Dado Customer Support" — the same label
 * the app uses elsewhere to hide Dado staff from customer-facing views. When
 * the executor has no user record in this org (e.g. a Dado admin who lives
 * outside the org's user collection), the document listener resolves to `null`
 * (not loading) — fall back to the raw id rather than spinning forever.
 */
const MergeExecutorName = ({ executor }: { executor: string }) => {
  const currentUser = useCurrentUser();
  const [user, loading] = useUserById(executor);

  // The executor is usually the current admin (they just ran the merge). Dado
  // admins have no org user record, so resolve identity from the current-user
  // session when the executor is the viewer.
  const isSelf = executor === currentUser.id;
  const email = user?.data.email ?? (isSelf ? currentUser.email : undefined);

  if (loading && !isSelf) {
    return <InlineLoader />;
  }
  if (isDadoEmailAddress(email)) {
    return <>{'Dado Customer Support'}</>;
  }
  if (user) {
    return <>{toFullName(user.data)}</>;
  }
  if (isSelf) {
    return <>{toFullName(currentUser) || currentUser.email}</>;
  }
  return <>{executor}</>;
};

const UserProfileHistory = ({
  userId,
  org
}: {
  userId: string;
  org: IOrganization;
}) => {
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();
  const orgId = org.id;
  const [v] = useUserHistoryData(orgId, userId);
  const [changes] = useUserImportHistory(orgId, userId);

  return (
    <FlexContainerVertical spacing={2}>
      <FlexContainerVertical spacing={0.5}>
        <strong>{t('User created')}</strong>
        <div>
          {v ? (
            (() => {
              // Defensive: v.createTime can be a non-Timestamp shape for users
              // with a malformed/partial history record. Same root cause as the
              // import-change defensive block below.
              const ms = timestampToMillisSafe(v.createTime);
              return ms
                ? moment(ms).format('YYYY/MM/DD HH:mm z')
                : t('(unknown)');
            })()
          ) : (
            <InlineLoader />
          )}
        </div>
      </FlexContainerVertical>

      <FlexContainerVertical spacing={0.5}>
        <strong>{t('Imports affecting this user')}</strong>
        <div>
          {changes ? (
            <ImportChangesGrid>
              {changes.map((c) => {
                // Merge records share this stream but have a different shape
                // (type 'M', with mergedAt/mergedFromUserId/executor).
                if (c.data.type === 'M') {
                  const mergedAtMs = timestampToMillisSafe(c.data.mergedAt);
                  const mergedAtStr = mergedAtMs
                    ? moment(mergedAtMs).format('YYYY/MM/DD HH:mm z')
                    : t('(unknown)');
                  return (
                    <React.Fragment key={c.id}>
                      <Typography
                        variant="body2"
                        style={{ alignSelf: 'center', padding: '6px 0' }}
                      >
                        {mergedAtStr}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ alignSelf: 'center' }}
                      >
                        {t('Merged with user {{ userId }}', {
                          userId: c.data.mergedFromUserId
                        })}{' '}
                        · <MergeExecutorName executor={c.data.executor} />
                      </Typography>
                    </React.Fragment>
                  );
                }
                // Defensive: an import that crashed mid-commit (e.g.
                // mutateUsersBatch threw) can leave the history record with
                // missing/malformed timestamps. toMoment() blows up on a
                // non-Timestamp, which used to take down the whole tab.
                // Fall back to a placeholder so the rest of the row renders.
                const committedAtMs = timestampToMillisSafe(c.data.committedAt);
                const importedAtMs = timestampToMillisSafe(c.data.importCreatedAt);
                const committedAtStr = committedAtMs
                  ? moment(committedAtMs).format('YYYY/MM/DD HH:mm z')
                  : t('(unknown)');
                const importedAtStr = importedAtMs
                  ? moment(importedAtMs).format('YYYY/MM/DD HH:mm z')
                  : t('(unknown)');
                return (
                <React.Fragment key={c.id}>
                  <Link to={ROUTES.users.imports.details.url(c.data.importId)}>
                    <Button color="primary" size="small">
                      <Tooltip
                        title={`${t('Applied at {{ date }}', {
                          date: committedAtStr
                        })}`}
                      >
                        <div>{importedAtStr}</div>
                      </Tooltip>
                    </Button>
                  </Link>
                  <Typography variant="caption">
                    {c.data.type === 'A' && t('User added')}
                    {c.data.type === 'C' &&
                      Object.keys(c.data.changes).sort().join(', ')}
                    {c.data.type === 'R' && t('User removed')}
                  </Typography>
                </React.Fragment>
                );
              })}
            </ImportChangesGrid>
          ) : (
            <InlineLoader />
          )}
        </div>
      </FlexContainerVertical>
    </FlexContainerVertical>
  );
};

const OpenExperienceHubButton = ({
  orgId,
  userId,
  name
}: {
  orgId: string;
  userId: string;
  name: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [url, loading] = useEmployeeAppDashboardUrlForUserId(orgId, userId);
  const body = (
    <div>
      <strong>{t(`Open {{ name }}'s Experience Hub`, { name })} </strong>{' '}
      {t('Impersonate {{ name }} to see their tasks and messages', {
        name
      })}
    </div>
  );

  const iconSize = 16;
  if (loading) {
    return (
      <Button
        color="primary"
        variant="text"
        disabled={true}
        startIcon={<InlineLoader size={iconSize} />}
      >
        {body}
      </Button>
    );
  }
  if (!url) {
    return null;
  }

  return (
    <Button
      startIcon={<ExternalLink size={iconSize} />}
      color="primary"
      variant="text"
      href={url}
      target="_blank"
    >
      {body}
    </Button>
  );
};

const OpenExperienceHubBanner = ({
  orgId,
  userId,
  name
}: {
  orgId: string;
  userId: string;
  name: string;
}) => {
  return (
    <InfoBox color="gray" margin="dense">
      <OpenExperienceHubButton orgId={orgId} userId={userId} name={name} />
    </InfoBox>
  );
};



const ContentUserProfile = ({ userId }: { userId: string }) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const [user, loadingUser, errorUser] = useUserById(userId);
  useErrorLogger('user', errorUser);
  // Org UI settings (pinned profile fields) are a non-critical UI preference and
  // are loaded independently: a failed/denied read must NOT take down the whole
  // profile. It falls back to USER_DATA_GRID_MINIMAL_FIELDS below. (Previously
  // this was combined into `errorUser`, so a permission-denied on the
  // admin-gated orgUiSettings doc crashed the entire page for non-admins.)
  const [orgUiSettings, , errorOrgUiSettings] = useOrgUiSettings(org.id);
  useErrorLogger('orgUiSettings', errorOrgUiSettings);

  const [lastSavedField, setLastSavedField] = useState<{
    fieldPath: string;
    value: unknown;
  } | null>(null);
  useEffect(() => {
    setLastSavedField(null);
  }, [user]);

  const pinnedFields = useMemo<(StandardUserFieldKey | CustomFieldKey)[]>(
    () =>
      orgUiSettings?.data.userProfile.pinnedFields.map((f) => f.field) ||
      USER_DATA_GRID_MINIMAL_FIELDS,
    [orgUiSettings]
  );

  const [tab, setTab] = useTypedStringQueryParam<
    'experiences' | 'moments' | 'data' | 'history'
  >('tab', 'experiences');

  if (loadingUser) {
    return <PageLoader />;
  }
  if (errorUser) {
    return (
      <SomethingWentWrongWithReport
        error={errorUser}
        context={{ page: 'user_profile' }}
      />
    );
  }
  if (!user) {
    return <NotFound />;
  }

  return (
    <>
      <PageBody noTopPadding>
        <PageToolbar sticky offset={DEFAULT_OFFSET}>
          <PageToolbarTitle>
            <FlexContainer
              fullWidth
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography component="h1" variant="h1">
                {toFullName(user.data)}
              </Typography>
              <MergeEmployeeButton orgId={org.id} profileUser={user} />
            </FlexContainer>
          </PageToolbarTitle>
        </PageToolbar>
        <Section>
          <FlexContainerVertical spacing={2}>
              <OpenExperienceHubBanner
                orgId={org.id}
                userId={userId}
                name={user.data.preferredName || user.data.firstName}
              />
            <UserDataGrid d={user} fields={pinnedFields} org={org} />
          </FlexContainerVertical>
        </Section>

        <Section>
          <Tabs
            value={tab}
            onChange={(ev, tabName) => setTab(tabName)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="experiences" label={t('Experiences')} />
            <Tab value="moments" label={t('Moments')} />
            <Tab value="data" label={t('Profile Data')} />
            {isDadoEmployee && <Tab value="history" label={t('History')} />}
          </Tabs>

          <TabPanel active={tab === 'experiences'}>
            <UserProfileExperiences org={org} userId={userId} />
          </TabPanel>

          <TabPanel active={tab === 'moments'}>
            <UserProfileMoments org={org} user={user} />
          </TabPanel>

          <TabPanel active={tab === 'data'}>
            <UserProfileData
              org={org}
              user={user}
              pinnedFields={pinnedFields}
              lastSavedField={lastSavedField}
              onFieldSaved={(fieldPath, value) =>
                setLastSavedField({ fieldPath, value })
              }
            />
          </TabPanel>

          <TabPanel active={tab === 'history'}>
            <UserProfileHistory org={org} userId={userId} />
          </TabPanel>
        </Section>
      </PageBody>
    </>
  );
};

export const PageUserProfile = ({ userId }: { userId: string }) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.profile)) {
    return <NoAccess />;
  }

  return <ContentUserProfile userId={userId} />;
};

const ContentUserMerge = ({
  userId,
  otherUserId
}: {
  userId: string;
  otherUserId: string;
}) => {
  const { org } = useCurrentUser();
  const { ROUTES, goTo } = useRoutes();
  const [user, loadingUser, errorUser] = useUserById(userId);
  useErrorLogger('user', errorUser);

  if (loadingUser) {
    return <PageLoader />;
  }
  if (errorUser) {
    return (
      <SomethingWentWrongWithReport
        error={errorUser}
        context={{ page: 'user_merge' }}
      />
    );
  }
  if (!user) {
    return <NotFound />;
  }

  return (
    <MergeClashResolution
      profileUser={user}
      org={org}
      otherUserId={otherUserId}
      onCancel={() => goTo(ROUTES.users.profile.url(userId))}
    />
  );
};

export const PageUserMerge = ({
  userId,
  otherUserId
}: {
  userId: string;
  otherUserId: string;
}) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.profile)) {
    return <NoAccess />;
  }

  return <ContentUserMerge userId={userId} otherUserId={otherUserId} />;
};
